import { clear, el, setText } from '../utils/dom.js';
import { formatPace, formatTime } from '../ultra/ultraPacing.js';

function renderStatsGrid(container, items) {
  clear(container);
  for (const item of items) {
    container.appendChild(
      el('div', { class: 'stat-item' }, [
        el('div', { class: 'stat-value', text: item.value }),
        el('div', { class: 'stat-label', text: item.label })
      ])
    );
  }
}

export function renderSummaryStats(container, data) {
  const items = [
    { value: data.totalDistanceKm.toFixed(2), label: 'Total Distance (km)' },
    { value: data.totalGAPDistanceKm.toFixed(2), label: 'Total GAP Distance (km)' },
    { value: data.runnableDistanceKm.toFixed(2), label: 'Runnable Distance (km)' },
    { value: `${data.runnablePercentage}%`, label: 'Runnable Percentage' },
    { value: String(data.totalSegments), label: 'Total Segments' },
    { value: String(data.totalSections), label: 'Total Sections' },
    { value: String(Math.round(data.totalElevationGainM)), label: 'Total Elevation Gain (m)' },
    { value: String(Math.round(data.totalElevationLossM)), label: 'Total Elevation Loss (m)' },
    { value: `${data.avgGradientPercent}%`, label: 'Average Gradient' }
  ];
  renderStatsGrid(container, items);
}

export function renderUltraSummary(container, ultraPacing, { onExport } = {}) {
  clear(container);
  if (!ultraPacing) return;

  const root = el('div', { class: 'ultra-pacing-summary' });
  root.appendChild(el('h3', { text: 'Ultra Pacing Strategy' }));

  const tiles = el('div', { class: 'ultra-tiles' });

  const tileItems = [
    { value: formatTime(ultraPacing.totalTimeMinutes), label: 'Predicted Finish' },
    { value: `${formatPace(ultraPacing.overallAvgPace)}/km`, label: 'Avg Pace' },
    { value: `${formatPace(ultraPacing.gapAvgPace)}/km`, label: 'Avg GAP', sublabel: 'Flat equivalent effort' },
    { value: `${Math.abs(Math.round(ultraPacing.adjustedPerformanceDrop * 100))}%`, label: 'Performance Drop', sublabel: 'vs AT pace' },
    { value: `${formatPace(ultraPacing.maxRecommendedPace)}/km`, label: 'Max Pace Limit', sublabel: 'Never exceed' },
    { value: `${ultraPacing.cohort}/10`, label: 'Cohort', sublabel: ultraPacing.cohortName }
  ];

  for (const item of tileItems) {
    const tile = el('div', { class: 'ultra-tile' }, [
      el('div', { class: 'ultra-tile-value', text: item.value }),
      el('div', { class: 'ultra-tile-label', text: item.label }),
      item.sublabel ? el('div', { class: 'ultra-tile-sublabel', text: item.sublabel }) : null
    ]);
    tiles.appendChild(tile);
  }

  root.appendChild(tiles);

  const exportBtn = el('button', { class: 'export-btn', type: 'button' }, ['Export Pacing Plan (CSV)']);
  exportBtn.addEventListener('click', () => onExport?.());
  root.appendChild(exportBtn);

  container.appendChild(root);
}

function renderTable(tableEl, headers, rows) {
  clear(tableEl);

  const thead = el('thead');
  const headRow = el('tr');
  for (const h of headers) {
    const th = el('th', { text: h.label });
    if (h.class) th.className = h.class;
    headRow.appendChild(th);
  }
  thead.appendChild(headRow);
  tableEl.appendChild(thead);

  const tbody = el('tbody');
  for (const row of rows) {
    const tr = el('tr');
    for (const cell of row) {
      const td = el('td');
      if (cell.class) td.className = cell.class;
      if (cell.node) td.appendChild(cell.node);
      else setText(td, cell.text);
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  tableEl.appendChild(tbody);
}

export function renderGradientSummaryTable(tableEl, gradientSummary) {
  const headers = [
    { label: 'Gradient Range' },
    { label: 'Segment Count', class: 'numeric' },
    { label: 'Total Distance (km)', class: 'numeric' },
    { label: 'Total Elevation Change (m)', class: 'numeric' },
    { label: 'Percentage of Route', class: 'numeric' }
  ];

  const rows = gradientSummary.map(item => ([
    { text: item.gradientRange },
    { text: String(item.segmentCount), class: 'numeric' },
    { text: item.totalDistance.toFixed(2), class: 'numeric' },
    { text: item.totalElevationChange.toFixed(1), class: 'numeric' },
    { text: `${item.percentage}%`, class: 'numeric' }
  ]));

  renderTable(tableEl, headers, rows);
}

export function renderSectionStats(container, sectionStats) {
  clear(container);
  const items = [
    { value: String(sectionStats.uphill.count), label: 'Uphill Sections' },
    { value: String(sectionStats.flat.count), label: 'Flat Sections' },
    { value: String(sectionStats.downhill.count), label: 'Downhill Sections' },
    { value: (sectionStats.uphill.distance / 1000).toFixed(2), label: 'Total Uphill (km)' },
    { value: (sectionStats.flat.distance / 1000).toFixed(2), label: 'Total Flat (km)' },
    { value: (sectionStats.downhill.distance / 1000).toFixed(2), label: 'Total Downhill (km)' },
    { value: (sectionStats.uphill.longest / 1000).toFixed(2), label: 'Longest Uphill (km)' },
    { value: (sectionStats.flat.longest / 1000).toFixed(2), label: 'Longest Flat (km)' },
    { value: (sectionStats.downhill.longest / 1000).toFixed(2), label: 'Longest Downhill (km)' }
  ];

  for (const item of items) {
    container.appendChild(
      el('div', { class: 'section-stat' }, [
        el('div', { class: 'section-stat-value', text: item.value }),
        el('div', { class: 'stat-label', text: item.label })
      ])
    );
  }
}

export function renderSectionsTable(tableEl, sections) {
  const headers = [
    { label: 'Distance from Start (km)', class: 'numeric' },
    { label: 'Type' },
    { label: 'Segments' },
    { label: 'Distance (km)', class: 'numeric' },
    { label: 'Average Gradient (%)', class: 'numeric' },
    { label: 'Elevation Gain (m)', class: 'numeric' },
    { label: 'Elevation Loss (m)', class: 'numeric' },
    { label: 'Net Change (m)', class: 'numeric' }
  ];

  const rows = sections.map(section => {
    const typeNode = el('span', { class: `section-type ${section.class}`, text: section.type });
    const segRange = `${section.startSegment}${section.startSegment !== section.endSegment ? '-' + section.endSegment : ''} (${section.segmentCount})`;
    return [
      { text: (section.startDistance / 1000).toFixed(2), class: 'numeric' },
      { node: typeNode },
      { text: segRange },
      { text: (section.totalDistance / 1000).toFixed(2), class: 'numeric' },
      { text: section.averageGradient.toFixed(1), class: 'numeric' },
      { text: section.totalElevationGain.toFixed(1), class: 'numeric' },
      { text: section.totalElevationLoss.toFixed(1), class: 'numeric' },
      { text: section.netElevationChange.toFixed(1), class: 'numeric' }
    ];
  });

  renderTable(tableEl, headers, rows);
}

export function renderSegmentDetailsTable(tableEl, segments) {
  const headers = [
    { label: 'Segment' },
    { label: 'Distance (m)', class: 'numeric' },
    { label: 'Cumulative Distance (km)', class: 'numeric' },
    { label: 'Elevation Gain (m)', class: 'numeric' },
    { label: 'Elevation Loss (m)', class: 'numeric' },
    { label: 'Net Change (m)', class: 'numeric' },
    { label: 'Average Gradient (%)', class: 'numeric' },
    { label: 'Start Elevation (m)', class: 'numeric' },
    { label: 'End Elevation (m)', class: 'numeric' }
  ];

  let cumulativeDistance = 0;
  const rows = segments.map(seg => {
    cumulativeDistance += seg.distance;
    const cumKm = cumulativeDistance / 1000;
    return [
      { text: String(seg.segmentNumber) },
      { text: String(Math.round(seg.distance)), class: 'numeric' },
      { text: cumKm.toFixed(2), class: 'numeric' },
      { text: seg.elevationGain.toFixed(1), class: 'numeric' },
      { text: seg.elevationLoss.toFixed(1), class: 'numeric' },
      { text: seg.netElevationChange.toFixed(1), class: 'numeric' },
      { text: seg.averageGradient.toFixed(1), class: 'numeric' },
      { text: String(Math.round(seg.startElevation)), class: 'numeric' },
      { text: String(Math.round(seg.endElevation)), class: 'numeric' }
    ];
  });

  renderTable(tableEl, headers, rows);
}

